import { useState, useRef } from 'react';
import { Parameter } from '@shared/types';
import { getParameterIcon } from '@/utils/parameterUtils';
import { ParameterLabel } from '@/components/parameter/ParameterLabel';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { GripVertical, Plus, X } from 'lucide-react';

// ---------------------------------------------------------------------------
// Colour palette — same as GridInput for visual consistency
// ---------------------------------------------------------------------------

const OPTION_COLOURS = [
  { bg: 'hsl(220 70% 55%)', text: '#fff' }, // blue
  { bg: 'hsl(350 65% 55%)', text: '#fff' }, // red
  { bg: 'hsl(145 55% 45%)', text: '#fff' }, // green
  { bg: 'hsl(35 85% 55%)', text: '#fff' }, // amber
  { bg: 'hsl(280 60% 58%)', text: '#fff' }, // purple
  { bg: 'hsl(185 60% 45%)', text: '#fff' }, // teal
  { bg: 'hsl(20 70% 52%)', text: '#fff' }, // orange
  { bg: 'hsl(60 55% 50%)', text: '#000' }, // yellow
];

const EMPTY_COLOUR = { bg: 'hsl(0 0% 20%)', text: 'hsl(0 0% 55%)' };

/** Pick white or black text depending on the perceived brightness of a hex bg. */
function contrastText(hex: string): string {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h[0] + h[0] + h[1] + h[1] + h[2] + h[2] : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#000' : '#fff';
}

function optionColour(
  option: string,
  options: string[],
  customColours?: Record<string, string>,
): { bg: string; text: string } {
  const isBlank = /empty|none|blank|—/i.test(option);
  if (isBlank) return EMPTY_COLOUR;
  const custom = customColours?.[option];
  if (custom) return { bg: custom, text: contrastText(custom) };
  const idx = options.indexOf(option);
  if (idx < 0) return EMPTY_COLOUR;
  return OPTION_COLOURS[idx % OPTION_COLOURS.length];
}

// ---------------------------------------------------------------------------
// Shallow array equality — avoids unnecessary re-renders
// ---------------------------------------------------------------------------

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// StackInput
// ---------------------------------------------------------------------------

export function StackInput({
  param,
  onCommit,
}: {
  param: Parameter;
  onCommit: (value: string[]) => void;
}) {
  const { stackConfig } = param;
  const options = stackConfig?.options ?? [];
  const customColours = stackConfig?.optionColours;
  const min = stackConfig?.min;
  const max = stackConfig?.max;

  // Initialise from param.value, falling back to a single-item list
  const initialItems = (() => {
    if (Array.isArray(param.value) && (param.value as string[]).length > 0) {
      return param.value as string[];
    }
    return options.length > 0 ? [options[0]] : [];
  })();

  const [items, setItems] = useState<string[]>(initialItems);

  // Keep local state in sync when the parent passes a new param reference
  const prevValueRef = useRef<string[]>(initialItems);
  const incoming = Array.isArray(param.value) ? (param.value as string[]) : [];
  if (!arraysEqual(prevValueRef.current, incoming)) {
    prevValueRef.current = incoming;
    setItems(incoming);
  }

  // Which item's replace-popover is open (index, or null)
  const [openReplace, setOpenReplace] = useState<number | null>(null);
  // Whether the "add" popover is open
  const [addOpen, setAddOpen] = useState(false);

  // Drag state
  const dragIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const commit = (next: string[]) => {
    setItems(next);
    onCommit(next);
  };

  // ── Replace item at index with a new option ──────────────────────────────
  const handleReplace = (index: number, option: string) => {
    const next = items.map((v, i) => (i === index ? option : v));
    setOpenReplace(null);
    commit(next);
  };

  // ── Remove item at index ─────────────────────────────────────────────────
  const handleRemove = (index: number) => {
    const next = items.filter((_, i) => i !== index);
    commit(next);
  };

  // ── Append a new item ────────────────────────────────────────────────────
  const handleAdd = (option: string) => {
    const next = [...items, option];
    setAddOpen(false);
    commit(next);
  };

  // ── Drag-and-drop reorder (HTML5 drag API) ───────────────────────────────
  const handleDragStart = (index: number) => {
    dragIndexRef.current = index;
  };

  const handleDragEnter = (index: number) => {
    if (dragIndexRef.current === null || dragIndexRef.current === index) return;
    setDragOverIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault(); // required to allow drop
  };

  const handleDrop = (index: number) => {
    const from = dragIndexRef.current;
    if (from === null || from === index) {
      dragIndexRef.current = null;
      setDragOverIndex(null);
      return;
    }
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(index, 0, moved);
    dragIndexRef.current = null;
    setDragOverIndex(null);
    commit(next);
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    setDragOverIndex(null);
  };

  // ── Derived flags ────────────────────────────────────────────────────────
  const atMin = min !== undefined && items.length <= min;
  const atMax = max !== undefined && items.length >= max;

  const IconComponent = getParameterIcon(param.name);

  return (
    <div className="grid w-full grid-cols-[96px_1fr] items-start gap-3">
      <ParameterLabel
        param={param}
        IconComponent={IconComponent}
        className="pt-1"
      />

      <div className="flex flex-col gap-1.5">
        {/* Item list */}
        <div className="flex flex-col gap-px overflow-hidden rounded-md border border-border">
          {items.map((item, index) => {
            const colour = optionColour(item, options, customColours);
            const isDragTarget =
              dragOverIndex === index && dragIndexRef.current !== index;

            return (
              <div
                key={`${item}-${index}`}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragEnter={() => handleDragEnter(index)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(index)}
                onDragEnd={handleDragEnd}
                className={`group flex items-center gap-1.5 px-1.5 py-1 transition-colors ${
                  isDragTarget
                    ? 'bg-muted/60 outline outline-1 outline-primary'
                    : 'bg-background'
                }`}
              >
                {/* Drag handle */}
                <GripVertical className="h-3.5 w-3.5 flex-shrink-0 cursor-grab text-muted-foreground/40 group-hover:text-muted-foreground active:cursor-grabbing" />

                {/* Colour swatch + replace popover */}
                <Popover
                  open={openReplace === index}
                  onOpenChange={(open) =>
                    setOpenReplace(open ? index : null)
                  }
                >
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="flex min-w-0 flex-1 items-center gap-1.5 rounded px-1 py-0.5 text-left transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      aria-label={`Item ${index + 1}: ${item} — click to change`}
                    >
                      <span
                        className="inline-block h-3 w-3 flex-shrink-0 rounded-sm"
                        style={{ backgroundColor: colour.bg }}
                      />
                      <span
                        className="truncate font-mono text-xs font-semibold"
                        style={{ color: 'inherit' }}
                      >
                        {item}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-auto p-1.5"
                    side="bottom"
                    align="start"
                    sideOffset={4}
                  >
                    <div className="flex flex-col gap-0.5">
                      {options.map((opt) => {
                        const c = optionColour(opt, options, customColours);
                        const isActive = opt === item;
                        return (
                          <button
                            key={opt}
                            type="button"
                            className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs transition-colors hover:bg-muted/60"
                            style={{ fontWeight: isActive ? 700 : 400 }}
                            onClick={() => handleReplace(index, opt)}
                          >
                            <span
                              className="inline-block h-3 w-3 flex-shrink-0 rounded-sm"
                              style={{ backgroundColor: c.bg }}
                            />
                            {opt}
                            {isActive && (
                              <span className="ml-auto text-[10px] text-muted-foreground">
                                ✓
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Remove button — hidden when at minimum length */}
                {!atMin && (
                  <button
                    type="button"
                    className="ml-auto flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-sm text-muted-foreground/40 opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100 focus:outline-none focus-visible:opacity-100 focus-visible:ring-1 focus-visible:ring-ring"
                    aria-label={`Remove item ${index + 1}`}
                    onClick={() => handleRemove(index)}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Add button — hidden when at maximum length */}
        {!atMax && (
          <Popover open={addOpen} onOpenChange={setAddOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 self-start rounded px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                aria-label="Add item"
              >
                <Plus className="h-3 w-3" />
                Add
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto p-1.5"
              side="bottom"
              align="start"
              sideOffset={4}
            >
              <div className="flex flex-col gap-0.5">
                {options.map((opt) => {
                  const c = optionColour(opt, options, customColours);
                  return (
                    <button
                      key={opt}
                      type="button"
                      className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs transition-colors hover:bg-muted/60"
                      onClick={() => handleAdd(opt)}
                    >
                      <span
                        className="inline-block h-3 w-3 flex-shrink-0 rounded-sm"
                        style={{ backgroundColor: c.bg }}
                      />
                      {opt}
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Legend */}
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {options.map((opt) => {
            const c = optionColour(opt, options, customColours);
            return (
              <span
                key={opt}
                className="flex items-center gap-1 text-[10px] text-muted-foreground"
              >
                <span
                  className="inline-block h-2 w-2 flex-shrink-0 rounded-sm"
                  style={{ backgroundColor: c.bg }}
                />
                {opt}
              </span>
            );
          })}
          {(min !== undefined || max !== undefined) && (
            <span className="text-[10px] text-muted-foreground/50">
              {min !== undefined && max !== undefined
                ? `${min}–${max} items`
                : min !== undefined
                  ? `min ${min}`
                  : `max ${max}`}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
